/**
 * Firestore-backed RTK Query API for the building / workplace view.
 *
 * Used within Building.tsx to directly read from the same Firestore collection as kantoordagDayViewApi, but
 * with a live onSnapshot listener.
 *
 * Reads from the same Firestore collection as kantoordagDayViewApi, but
 * selects different document types:
 *   - ROOM-{roomName}  →  room occupancy
 *   - waitlist         →  waitlist users
 *   - overview         →  building-level summary (seatsAvailable, capacity, …)
 *
 * Defaults are required for ROOM-* documents: a room doc is only written to
 * Firestore when at least one reservation is made in that room on that day.
 * The defaults (views/defaults/{building}/ROOM-*) supply the static room
 * structure (name, totalSeats) with zero occupancy.
 */

import { collection, CollectionReference, Firestore, QuerySnapshot } from 'firebase/firestore';
import { RoomUser, RoomWithUsers, WorkplaceView } from '../kantoordagApi';
import { createFirestoreStreamApi } from '../firestoreModel';
import { kantoordagDateKey } from './firestoreUtils';

// ---------------------------------------------------------------------------
// Firestore document ID constants (mirrors Kotlin)
// ---------------------------------------------------------------------------

const OVERVIEW_DOC = 'overview';
const WAITLIST_DOC = 'waitlist';
const ROOM_PREFIX = 'ROOM-';

// ---------------------------------------------------------------------------
// Low-level mappers
// ---------------------------------------------------------------------------

type RawDoc = Record<string, unknown>;

function mapRoomUser(raw: RawDoc): RoomUser {
    return {
        userId: raw.userId as string ?? '',
        userName: raw.userName as string ?? '',
        picture: (raw.picture as string | null | undefined) ?? null,
        company: raw.company as string ?? '',
        companyLogo: raw.companyLogo as string ?? '',
        room: raw.room as string ?? '',
    };
}

function mapRoomWithUsers(docId: string, raw: RawDoc): RoomWithUsers {
    const roomName = (raw.room as string) ?? docId.substring(ROOM_PREFIX.length);
    const totalSeats = (raw.totalSeats as number) ?? 0;
    const blocked = (raw.blocked as boolean) ?? false;
    const users: RoomUser[] = ((raw.assignedUsers as RawDoc[]) ?? []).map(mapRoomUser);
    const assigned = users.length;
    const seatsAvailable = blocked ? 0 : Math.max(0, totalSeats - assigned);
    const capacityPercentage = blocked
        ? 100
        : totalSeats > 0
            ? Math.round((assigned * 100) / totalSeats)
            : 0;

    return { room: roomName, totalSeats, seatsAvailable, assigned, capacityPercentage, users, blocked };
}

// ---------------------------------------------------------------------------
// Snapshot → WorkplaceView
// ---------------------------------------------------------------------------

type RoomMap = Map<string, RoomWithUsers>;

const emptyResponse = (): WorkplaceView => ({
    seatsAvailable: 0,
    onWaitList: 0,
    totalSeats: 0,
    capacityPercentage: 0,
    rooms: [],
    waitlist: [],
});

function mapData(snapshot: QuerySnapshot, defaultsSnapshot: QuerySnapshot | null): WorkplaceView {
    let seatsAvailable = 0;
    let onWaitList = 0;
    let totalSeats = 0;
    let capacityPercentage = 0;
    let hasOverview = false;
    const rooms: RoomMap = new Map();
    let waitlist: RoomUser[] = [];

    // 1. Read real data for the day
    snapshot.forEach((doc) => {
        const raw = doc.data() as RawDoc;
        if (doc.id === OVERVIEW_DOC) {
            hasOverview = true;
            seatsAvailable = (raw.seatsAvailable as number) ?? 0;
            onWaitList = (raw.waitlistSize as number) ?? 0;
            totalSeats = (raw.capacity as number) ?? 0;
            capacityPercentage = (raw.capacityPercentage as number) ?? 0;
        } else if (doc.id === WAITLIST_DOC) {
            waitlist = ((raw.users as RawDoc[]) ?? []).map(mapRoomUser);
        } else if (doc.id.startsWith(ROOM_PREFIX)) {
            rooms.set(doc.id, mapRoomWithUsers(doc.id, raw));
        }
        // Company docs are ignored here — that's DayOverview's concern
    });

    // 2. Fill in defaults for anything missing
    if (defaultsSnapshot) {
        defaultsSnapshot.forEach((doc) => {
            const raw = doc.data() as RawDoc;
            if (doc.id === OVERVIEW_DOC && !hasOverview) {
                seatsAvailable = (raw.seatsAvailable as number) ?? (raw.capacity as number) ?? 0;
                totalSeats = (raw.capacity as number) ?? 0;
                capacityPercentage = 0;
                onWaitList = 0;
            } else if (doc.id.startsWith(ROOM_PREFIX) && !rooms.has(doc.id)) {
                rooms.set(doc.id, mapRoomWithUsers(doc.id, raw));
            }
        });
    }

    return {
        seatsAvailable,
        onWaitList,
        totalSeats,
        capacityPercentage,
        rooms: Array.from(rooms.values()),
        waitlist,
    };
}

// ---------------------------------------------------------------------------
// RTK Query API
// ---------------------------------------------------------------------------

type BuildingViewArg = { date: string; building: string };

export const kantoordagBuildingViewStreamingApi = createFirestoreStreamApi<WorkplaceView, BuildingViewArg>({
    reducerPath: 'kantoordagBuildingViewApi',

    getRef: (db: Firestore, { date, building }: BuildingViewArg): CollectionReference =>
        collection(db, 'Kantoordag', kantoordagDateKey(date), building),

    getDefaultsRef: (db: Firestore, { building }: BuildingViewArg): CollectionReference =>
        collection(db, 'views', 'defaults', building),

    mapData,
    emptyResult: emptyResponse,
});

export const useGetBuildingViewQuery = kantoordagBuildingViewStreamingApi.endpoints.get.useQuery;
